import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import dateFormat from 'dateformat';
import { compact, get, flatten } from 'lodash-es';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Button from 'reactstrap/lib/Button';
import { CmsEntities } from 'client/data/models/cms';
import { getValidImageUrl } from 'client/utils/image-helpers';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { Link } from 'site-modules/shared/components/link/link';
import { SocialShareDialog } from 'site-modules/shared/components/social-share-dialog/social-share-dialog';

import './dynamic-leaderboard.scss';

const THEME = { THEME_A: 'themeA', THEME_B: 'themeB' };
const BAR_CLASS_NAME = ['secondary-bar', 'primary-bar'];

const BASE_WIDTH = 20;
const MAX_WIDTH = {
  true: 66,
  false: 80,
};

function getBarSettings(vehicles, maxWidth) {
  const variableWidth = maxWidth - BASE_WIDTH;
  const metricValues = flatten(
    vehicles.map(vehicle => [get(vehicle, 'primaryMetricValue', 0), get(vehicle, 'secondaryMetricValue', 0)])
  );
  const minValue = Math.min(...metricValues);
  const maxValue = Math.max(...metricValues);

  return { baseValue: minValue, percentValue: (maxValue - minValue) / variableWidth };
}

function getBarWidth({ barSettings: { baseValue, percentValue }, value }) {
  const valueNumber = value ? BASE_WIDTH + (value - baseValue) / percentValue : BASE_WIDTH / 2;

  return `${valueNumber.toFixed(1)}%`;
}

export function DynamicLeaderboard({ entry = null, pathname = null, isWidget = false, isMobile = false }) {
  const [isModalOpen, toggleModalOpen] = useToggle();

  if (!entry) {
    return null;
  }

  const leaderboardId = (isWidget ? entry.id : entry.metadata('filePath').value()).split('/').pop();

  const config = entry.child('leaderboard-config');
  const theme = config.metadata('theme').value(THEME.THEME_A);
  const title = config.metadata('title').value();
  const subtitle = config.metadata('subtitle').value();
  const footerDisclaimer = config.metadata('footerDisclaimer').value();
  const updateDate = config.metadata('updateDate').value();
  const enableEmbedding = config.metadata('enableEmbedding').boolean();

  const images = config.links();
  const logo = images.find(({ rel }) => rel === 'logo');
  const titleImage = images.find(({ rel }) => rel === 'title-image');
  const footerImage = images.find(({ rel }) => rel === 'footer-image');

  const primaryMetricKey = config.metadata('primaryMetric').value();
  const secondaryMetricKey = config.metadata('secondaryMetric').value();
  const metricsConfig = [
    {
      key: secondaryMetricKey,
      name: config.child(secondaryMetricKey).metadata('metricName').value(),
    },
    {
      key: primaryMetricKey,
      name: config.child(primaryMetricKey).metadata('metricName').value(),
    },
  ];

  const vehicles = entry
    .child('vehicles')
    .children()
    .map(vehicleEntry => ({
      vehicleName: vehicleEntry.metadata('vehicleName').value(),
      href: vehicleEntry.metadata('href').value(),
      imageUrl: vehicleEntry.metadata('imageURL').value(),
      primaryMetric: vehicleEntry.metadata(primaryMetricKey).value(),
      primaryMetricValue: vehicleEntry.metadata(`${primaryMetricKey}Value`).value(),
      secondaryMetric: vehicleEntry.metadata(secondaryMetricKey).value(),
      secondaryMetricValue: vehicleEntry.metadata(`${secondaryMetricKey}Value`).value(),
    }));
  const barSettings = getBarSettings(vehicles, MAX_WIDTH[isMobile]);

  return (
    <div className="dynamic-leaderboard" data-tracking-parent="leaderboard">
      <div
        className={classnames('leaderboard-container rounded-12 pos-r py-1_5 px-0_5 px-md-1_5', theme, {
          'bg-blue-100': theme === THEME.THEME_A,
          'bg-green-90': theme === THEME.THEME_B,
        })}
      >
        <div className="d-flex flex-wrap justify-content-between align-items-start mb-0_75">
          <div>
            {!!titleImage && (
              <div className="mb-0_5">
                <img
                  className="title-image"
                  src={getValidImageUrl(titleImage.href)}
                  alt={titleImage.title || ''}
                  {...(isMobile ? { width: '100%' } : { height: '80' })}
                  loading="lazy"
                />
              </div>
            )}
            {!!logo && (
              <div className="mb-0_5">
                <img src={getValidImageUrl(logo.href)} alt={logo.title || ''} height={18} loading="lazy" />
              </div>
            )}
            {!!title && (
              <h2 className={classnames('heading-3', { 'mb-0_25': subtitle, 'mb-0_75': !subtitle })}>{title}</h2>
            )}
            {!!subtitle && <div className="size-20 mb-0_75">{subtitle}</div>}
          </div>
          {enableEmbedding && (
            <Button
              color="outline-blue-50"
              onClick={toggleModalOpen}
              className="btn-bg-white fw-medium size-16 mb-0_75 px-0_75 py-0_25"
              data-tracking-id="open_embed_modal"
            >
              Embed leaderboard
            </Button>
          )}
        </div>
        <div className="mb-1">
          <div className="legend d-inline-block d-md-flex">
            {compact(
              metricsConfig.map(
                ({ key, name }, index) =>
                  !!key && (
                    <div key={key} className="d-flex align-items-center">
                      <div className={classnames('legend-bar me-0_5', BAR_CLASS_NAME[index])} />
                      <span className="text-start">{name}</span>
                    </div>
                  )
              )
            )}
          </div>
        </div>
        <ul className="vehicles-list list-unstyled mb-0 rounded-12 overflow-hidden mb-1">
          {vehicles.map(
            (
              { vehicleName, href, imageUrl, primaryMetric, secondaryMetric, primaryMetricValue, secondaryMetricValue },
              index
            ) => {
              const vehicleNameProps = href
                ? { tag: Link, to: href, 'data-tracking-id': 'go_to_review', target: isWidget ? '_blank' : undefined }
                : { tag: 'p' };

              return (
                <Row tag="li" key={vehicleName} className="list-item gx-0">
                  <Col xs={2} className="rating-image d-flex align-items-center align-items-md-end pos-r py-0_5">
                    <div
                      className={classnames('rating-text heading-3 mt-md-0_5', {
                        'pos-a top-0 left-0 mt-0_5': !isMobile,
                      })}
                    >
                      {index + 1}
                    </div>
                    {!isMobile && <img src={getValidImageUrl(imageUrl)} alt="" className="w-100" loading="lazy" />}
                  </Col>
                  <Col
                    {...vehicleNameProps}
                    xs={4}
                    md={3}
                    className={classnames(
                      'vehicle-name d-flex align-items-center bg-white mb-0 text-blue-40 px-0_25 py-0_5',
                      { 'size-16': !isMobile, medium: isMobile }
                    )}
                  >
                    {vehicleName}
                  </Col>
                  <Col xs={6} md={7} className="bg-white pe-md-1">
                    <div className="d-flex flex-column h-100 justify-content-center py-0_5">
                      {!!secondaryMetric && (
                        <div className="d-flex align-items-center small mb-0_25">
                          <div
                            className={classnames('me-0_5', BAR_CLASS_NAME[0])}
                            style={{
                              width: getBarWidth({ barSettings, value: secondaryMetricValue }),
                            }}
                          >
                            <span className="visually-hidden">{metricsConfig[0].name}</span>
                          </div>
                          <span>{secondaryMetric}</span>
                        </div>
                      )}
                      <div className="d-flex align-items-center medium">
                        <div
                          className={classnames('me-0_5', BAR_CLASS_NAME[1])}
                          style={{ width: getBarWidth({ barSettings, value: primaryMetricValue }) }}
                        >
                          <span className="visually-hidden">{metricsConfig[1].name}</span>
                        </div>
                        <span className="text-cool-gray-10 fw-medium">{primaryMetric}</span>
                      </div>
                    </div>
                  </Col>
                </Row>
              );
            }
          )}
        </ul>
        {!!footerDisclaimer && <p className="medium text-cool-gray-40 mb-1">{footerDisclaimer}</p>}
        <div className="d-flex justify-content-between align-items-center">
          {!!footerImage && (
            <img src={getValidImageUrl(footerImage.href)} alt={footerImage.title || ''} height={40} loading="lazy" />
          )}
          {!!updateDate && (
            <p className="medium text-cool-gray-40 mb-0 ms-auto">Updated {dateFormat(updateDate, 'mmmm d, yyyy')}</p>
          )}
        </div>
      </div>
      {enableEmbedding && (
        <SocialShareDialog
          className="embeddable-leaderboard-modal"
          isOpen={isModalOpen}
          toggle={toggleModalOpen}
          headingText="Embed Code"
          pageUrl={`https://www.edmunds.com${pathname}`}
          shareableWidgetData={{
            widgetName: `leaderboard/${leaderboardId}`,
            defaultWidth: 640,
            defaultHeight: 1600,
            minWidth: 290,
            minHeight: 800,
            defaultTitle: title || titleImage?.title,
            trackingCreativeId: 'leaderboard',
            hasCopyTracking: true,
          }}
          hideSocialIcons
        />
      )}
    </div>
  );
}

DynamicLeaderboard.propTypes = {
  entry: CmsEntities.Content,
  pathname: PropTypes.string,
  isWidget: PropTypes.bool,
  isMobile: PropTypes.bool,
};
